<template>
  <mia-form>
    <mia-wait :for="['loading provision options', 'loading used provision options', 'loading provision option groups']">
      <template #waiting>
        <div class="flex items-center justify-center py-8">
          <mia-wave-loader />
        </div>
      </template>

      <div class="flex items-center justify-between p-4 bg-gray-100 rounded-t">
        <h2 class="py-0 my-0 text-lg">
          <template v-if="filteredProvisions.length === 0">
            Für dieses Fahrzeug stehen zur Zeit leider keine passenden Prämien zur Verfügung
          </template>
          <template v-else>
            Für dieses Fahrzeug {{ filteredProvisions.length === 1 ? 'steht' : 'stehen' }} folgende <span class="text-orange-mia">{{ filteredProvisions.length }} Prämie{{ filteredProvisions.length !== 1 ? 'n' : '' }}</span> zur Verfügung
          </template>
        </h2>

        <mia-button
          v-if="toggleable"
          type="button"
          class="border border-gray-400 bg-gray-300 text-gray-800 px-3 py-2 rounded-md hover:bg-orange-mia hover:text-white hover:border-orange-mia focus:outline-none"
          @click="isCollapsed = !isCollapsed"
        >
          Prämien {{ isCollapsed ? 'anzeigen' : 'ausblenden' }}
        </mia-button>
      </div>

      <div
        v-show="!isCollapsed"
        class="mt-6"
      >
        <div>
          <mia-form-section-grid>
            <div class="px-4">
              <mia-car-edit-feature-input
                v-model="filters.query"
                label="Suchen"
              />
            </div>
            <div
              v-tooltip="disableProvisionGroups ? 'Prämiengruppen können nur ausgewählt werden, wenn noch keine regulären Prämien eingebucht wurden.' : ''"
              class="px-4"
            >
              <mia-feature-select
                v-model="filters.group"
                label="Prämiengruppe"
                :disabled="disableProvisionGroups"
                :options="provisionGroups"
                track-by="id"
                :name-by="getProvisionGroupLabel"
              />
            </div>
          </mia-form-section-grid>
        </div>
        <div
          v-if="showProvisionGroupNotice"
          class="px-4 mt-4 mb-4"
        >
          <mia-notice
            permanent
            type="error"
            obvious
          >
            <div class="py-2 text-xl">
              <span class="font-bold">Achtung!</span> Nicht alle Prämien der ausgewählten Rabattgruppen können auf das Fahrzeug angewendet werden.
            </div>
          </mia-notice>
        </div>
        <div>
          <h3 class="px-4 py-0 pb-2 my-0 mt-6 border-b border-orange-mia">
            Prämien
          </h3>
        </div>
        <div class="py-4 overflow-x-hidden overflow-y-auto max-h-160">
          <mia-provision-option-card
            v-for="provisionOption in filteredProvisions"
            :key="provisionOption.id"
            :option="provisionOption"
            :price="price"
            :disabled="isProvisionOptionDisabled(provisionOption)"
            :disabled-reason="getProvisionOptionDisabledReason(provisionOption)"
            :net-list-price="netListPrice"
            class="mx-4"
            @update="onUpdate"
          />
        </div>
      </div>
    </mia-wait>

    <template
      v-if="!isCollapsed"
      #actions
    >
      <mia-submit-button
        :disabled="$wait.any"
        :is-loading="$wait.is('updating provisions')"
        label="Gewählte Prämien einbuchen"
        type="button"
        @click="save"
      />
    </template>
  </mia-form>
</template>

<script>
import uniq from 'lodash/uniq'
import {
  getProvisionOptionsByCarData, getUsedProvisionOptions, addProvisionItems, getProvisionOptionGroupsByCarData
} from '@/api/provisions'
import OptionCard from '@/components/provisions/OptionCard.vue'
import Form from '@/components/ui/widgets/Form.vue'
import FormSectionGrid from '@/components/ui/widgets/FormSectionGrid.vue'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import FeatureSelect from '@/components/cars/edit/FeatureSelect.vue'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import HandlesBackendValidationErrors from '@/mixins/HandlesBackendValidationErrors'
import Notice from '@/components/cars/edit/Notice.vue'

export default {
  name: 'MiaProvisionOptions',

  components: {
    'mia-car-edit-feature-input': FeatureInput,
    'mia-form': Form,
    'mia-form-section-grid': FormSectionGrid,
    'mia-provision-option-card': OptionCard,
    'mia-submit-button': SubmitButton,
    'mia-feature-select': FeatureSelect,
    'mia-notice': Notice
  },

  mixins: [HandlesBackendValidationErrors],

  props: {
    price: {
      type: [String, Number],
      default: 0
    },

    netListPrice: {
      type: [String, Number],
      default: 0
    },

    disabledOptions: {
      type: Array,
      default() {
        return []
      }
    },

    disableProvisionGroups: {
      type: Boolean,
      default: false
    },

    toggleable: {
      type: Boolean,
      default: false
    },

    defaultCollapsed: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      filters: {
        query: '',
        group: ''
      },
      provisionGroups: [],
      isCollapsed: this.defaultCollapsed
    }
  },

  computed: {
    car: {
      get() {
        return this.$store.state.car.car
      }
    },

    provisions: {
      get() {
        return this.$store.state.provisions.provisions
      },

      set(provisions) {
        this.$store.commit('set', provisions)
      }
    },

    usedProvisions: {
      get() {
        return this.$store.state.provisions.usedProvisions
      },

      set(usedProvisions) {
        this.$store.commit('setUsed', usedProvisions)
      }
    },

    filteredProvisions() {
      let { provisions } = this

      if (this.filters.query !== '') {
        provisions = provisions.filter(provision => provision.item_name.toLowerCase().includes(this.filters.query.toLowerCase()))
      }

      if (this.filters.group !== '') {
        const validProvisionOptionIds = this.provisionGroups
          .find(group => parseInt(group.id, 10) === parseInt(this.filters.group, 10))
          .item_provision_options
          .map(provision => parseInt(provision.id, 10))

        if (validProvisionOptionIds.length > 0) {
          provisions = provisions.filter(provision => validProvisionOptionIds.includes(parseInt(provision.id, 10)))
        }
      }

      return provisions
    },

    bookedProvisions() {
      return this.provisions
        .filter(provision => provision.item_booked)
        .map(provision => ({
          id: provision.id,
          item_target: provision.item_target,
          item_fordealer: provision.item_fordealer,
          item_fordealer_paymode: provision.item_fordealer_paymode,
          item_custom_provision_fix: provision.item_fix,
          item_custom_provision_percent: provision.item_percent,
          item_comment: provision.item_comment
        }))
    },

    showProvisionGroupNotice() {
      if (this.disableProvisionGroups || !this.filters.group) {
        return false
      }

      const selectedGroup = this.provisionGroups.find(group => parseInt(group.id, 10) === parseInt(this.filters.group, 10))

      if (!selectedGroup) {
        return false
      }

      return selectedGroup.item_provision_options.length !== this.filteredProvisions.length
    }
  },

  watch: {
    car(car) {
      this.initCar(car)
    },

    'filters.group': function (group) {
      if (group === '') {
        this.provisions = this.provisions.map(provision => ({
          ...provision,
          item_booked: false
        }))
      } else {
        this.provisions = this.provisions.map((provision) => {
          const isProvisionGroupProvision = this.filteredProvisions.find(p => p.id === provision.id) !== undefined

          return {
            ...provision,
            item_booked: isProvisionGroupProvision
          }
        })
      }
    }
  },

  created() {
    this.initCar(this.car)
  },

  methods: {
    initCar(car) {
      if (car.id && car.id > 0) {
        this.loadUsedProvisionOptions(car.id)
      }

      this.loadProvisionOptions(car)
      this.loadProvisionOptionGroups(car)
    },

    async loadUsedProvisionOptions(carId) {
      this.$wait.start('asdasdasd used provision options blablub')
      this.usedProvisions = await getUsedProvisionOptions(carId)
      this.$wait.end('asdasdasd used provision options blablub')
    },

    async loadProvisionOptions(car) {
      this.$wait.start('loading provision options')

      this.provisions = (await getProvisionOptionsByCarData(car))
        .sort((a, b) => a.item_name.localeCompare(b.item_name, 'de'))
        .map(provision => ({
          id: provision.id,
          item_comment: provision.item_comment,
          item_default_for_dealer: provision.item_default_for_dealer,
          item_default_target: provision.item_default_target,
          item_deprecated: provision.item_deprecated,
          item_editable: provision.item_editable,
          item_fix: provision.item_fix,
          item_fordealer: provision.item_fordealer,
          item_fordealer_paymode: provision.item_fordealer_paymode,
          item_name: provision.item_name,
          item_percent: provision.item_percent,
          item_target: provision.item_target,
          item_filename: provision.item_filename,
          item_objecturl_aws: provision.item_objecturl_aws,
          item_excluded_provision_options: provision.item_excluded_provision_options,
          item_provision_comment: provision.item_provision_comment,
          item_conditions: provision.item_conditions || []
        }))

      this.$wait.end('loading provision options')
    },

    async loadProvisionOptionGroups(car) {
      this.$wait.start('loading provision option groups')
      this.provisionGroups = await getProvisionOptionGroupsByCarData(car)
      this.$wait.end('loading provision option groups')
    },

    async save() {
      this.$wait.start('updating provisions')

      try {
        await addProvisionItems(this.car.id, this.bookedProvisions)
        await this.loadProvisionOptions(this.car)

        window.eventBus.$emit('mia-info', {
          info: ['Daten erfolgreich gespeichert.']
        })

        this.$set(this.filters, 'group', '')
        this.$emit('booked')
      } catch (e) {
        console.log(e)
        this.handleBackendValidationErrors(e)

        window.eventBus.$emit('mia-error', {
          error: ['Fehler beim Speichern der Daten.']
        })
      } finally {
        this.$wait.end('updating provisions')
      }
    },

    isProvisionOptionDisabled(provisionOption) {
      // A provision option is disabled, if
      //  a. The provision option has been disabled via prop (:disabled-options)
      //  b. The provision option is included in any currently selected provision option's list of excluded provision options
      return this.disabledOptions.includes(provisionOption.id)
        || this.provisions
          .filter(opt => !!opt.item_booked)
          .some(opt => opt.item_excluded_provision_options.length > 0 && opt.item_excluded_provision_options.includes(provisionOption.id))
    },

    getProvisionOptionDisabledReason(provisionOption) {
      if (!this.isProvisionOptionDisabled(provisionOption)) {
        return ''
      }

      // Get all provision options that are currently selected to be booked and include the provision option
      // in their list of excluded provision options
      const provisionOptionsThatExcludeThisOne = this.provisions
        .filter(opt => Array.isArray(opt.item_excluded_provision_options) && opt.item_excluded_provision_options.length > 0 && opt.item_excluded_provision_options.includes(provisionOption.id))
        .map(opt => opt.item_name)

      const provisionOptionTitles = new Intl.ListFormat('de', { style: 'short', type: 'disjunction' }).format(uniq(provisionOptionsThatExcludeThisOne))

      return `Diese Prämie kann nicht in Kombination mit ${provisionOptionTitles} gebucht werden.`
    },

    getProvisionGroupLabel(group) {
      const provisionsCount = group.item_provision_options.length

      return `${group.item_name} (${provisionsCount} Prämie${provisionsCount === 1 ? '' : 'n'})`
    },

    onUpdate(provision) {
      this.provisions = this.provisions.map((pro) => {
        if (parseInt(pro.id, 10) === parseInt(provision.id, 10)) {
          return {
            ...pro,
            ...provision
          }
        }

        return pro
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  input:not([type="checkbox"]):not([type="radio"]),
  select,
  textarea {
    @apply font-normal;
    @apply w-full;

    &:disabled {
      @apply opacity-50;
    }
  }

  label:not([class="checkboxlabel"]) {
    @apply font-normal;
    @apply w-full;
  }

</style>
